import React, { useContext, useEffect, useState } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
  Stack,
} from "@mui/material";
import { useConfirm } from "../context/ConfirmContext";
import { useMessage } from "../context/MessageContext";
import CommonTable from "../components/CommonTable";

const VendorPayments = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { confirm } = useConfirm();
  const { showMessage } = useMessage();

  const [vendor, setVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [payments, setPayments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    amount: "",
    gstAmount: "",
    paymentMode: "",
    description: "",
    stockRequests: [],
    stockReceipts: [],
  });

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(
          `/vendors?orgId=${organization}&academicYear=${academicYear}`
        );
        setVendors(res.data);
        // if (res.data.length && !vendor) setVendor(res.data[0]._id);
      } catch {
        showMessage("Failed to load vendors", "error");
      }
    };
    if (organization && academicYear) fetchVendors();
  }, [organization, academicYear]);

  useEffect(() => {
    const fetchSupportingData = async () => {
      try {
        if (!vendor) return;
        const [reqs, recs, pays] = await Promise.all([
          axios.get(
            `/procurement/requests?orgId=${organization}&vendor=${vendor}&academicYear=${academicYear}`
          ),
          axios.get(
            `/procurement/receipts?orgId=${organization}&vendor=${vendor}&academicYear=${academicYear}`
          ),
          axios.get(
            `/procurement/payments?orgId=${organization}&vendor=${vendor}&academicYear=${academicYear}`
          ),
        ]);
        setRequests(reqs.data);
        setReceipts(recs.data);
        setPayments(pays.data);
      } catch {
        showMessage("Failed to load data", "error");
      }
      setLoading(false);
    };
    if (vendor && organization && academicYear) {
      setLoading(true);
      fetchSupportingData();
    }
  }, [vendor, organization, academicYear]);

  const handleSave = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0)
      return showMessage("Amount must be greater than 0", "warning");
    if (!form.paymentMode.trim())
      return showMessage("Payment mode is required", "warning");

    try {
      await axios.post("/procurement/payments", {
        ...form,
        organization,
        vendor,
        academicYear,
      });
      showMessage("Payment added successfully", "success");
      setOpen(false);
      // Refresh payments
      const res = await axios.get(
        `/procurement/payments?orgId=${organization}&vendor=${vendor}&academicYear=${academicYear}`
      );
      setPayments(res.data);
    } catch {
      showMessage("Failed to save payment", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Vendor Payments
      </Typography>

      <FormControl sx={{ mb: 2, minWidth: 250 }}>
        <InputLabel>Vendor</InputLabel>
        <Select
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          label="Vendor"
        >
          {vendors.map((v) => (
            <MenuItem key={v._id} value={v._id}>
              {v.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button variant="contained" onClick={() => setOpen(true)} sx={{ ml: 2 }}>
        Add Payment
      </Button>

      <CommonTable
        rows={payments}
        loading={loading}
        columns={[
          { label: "Description", field: "description" },
          { label: "Amount", field: "amount" },
          { label: "GST Amount", field: "gstAmount" },
          { label: "Payment Mode", field: "paymentMode" },
          {
            label: "Stock Requests",
            renderCell: (p) =>
              (p.stockRequests || [])
                .map((id) => {
                  const req = requests.find((r) => r._id === id);
                  return req ? req.description : id;
                })
                .join(", "),
          },
          {
            label: "Stock Receipts",
            renderCell: (p) =>
              (p.stockReceipts || [])
                .map((id) => {
                  const rec = receipts.find((r) => r._id === id);
                  return rec ? rec.deliveryNote : id;
                })
                .join(", "),
          },
          {
            label: "Created At",
            renderCell: (p) =>
              p.createdAt
                ? new Date(p.createdAt).toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                : "-",
          },
        ]}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Add Vendor Payment</DialogTitle>
        <DialogContent>
          <TextField
            label="Amount"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            type="number"
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="GST Amount"
            value={form.gstAmount}
            onChange={(e) =>
              setForm((f) => ({ ...f, gstAmount: e.target.value }))
            }
            type="number"
            fullWidth
            margin="normal"
          />
          <TextField
            label="Payment Mode"
            value={form.paymentMode}
            onChange={(e) =>
              setForm((f) => ({ ...f, paymentMode: e.target.value }))
            }
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm((f) => ({ ...f, description: e.target.value }))
            }
            fullWidth
            margin="normal"
          />
          <Autocomplete
            multiple
            options={requests}
            getOptionLabel={(opt) => opt.requestNumber}
            value={requests.filter((r) => form.stockRequests.includes(r._id))}
            onChange={(e, value) =>
              setForm((f) => ({
                ...f,
                stockRequests: value.map((v) => v._id),
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Link Stock Requests" />
            )}
            sx={{ mt: 2 }}
          />
          <Autocomplete
            multiple
            options={receipts}
            getOptionLabel={(opt) => opt.receiptNumber}
            value={receipts.filter((r) => form.stockReceipts.includes(r._id))}
            onChange={(e, value) =>
              setForm((f) => ({
                ...f,
                stockReceipts: value.map((v) => v._id),
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Link Stock Receipts" />
            )}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorPayments;
